import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import type { IListTabResponse, RedeemableApplicable, TabStatus } from "./Tabs";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";

export interface IListVoucher {
  active: boolean;
  applicable_to: RedeemableApplicable;
  balance: number;
  cap: number;
  code: string;
  email: string;
  id: number;
  identifier: string;
  message: string;
  name: string;
  status: TabStatus;
  applies_to_service_charge?: boolean;
}

export interface IVoucher extends IListVoucher {
  is_applicable: boolean;
  applicable_weekdays: number[];
  cap_per_head: number;
  discounted_menu_only: boolean;
  bill: unknown;
  percentage: number;
  restaurant_id: ID;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  timezone: string;
}

export interface IListVoucherParams {
  search?: string;
  isVenueSpecific?: boolean;
  page?: number;
  page_size?: number;
}

export interface IListVoucherResponse
  extends APIPaginatedResponse<IListVoucher> {}

export interface IListVoucherResponseAll {
  pages: Array<IListVoucherResponse>;
  pageParams: number[];
}

export interface IVoucherService {
  list: (
    params?: IListVoucherParams,
  ) => UseQueryResult<Array<IListVoucher>, Error>;
  listAll: (
    params?: IListVoucherParams,
  ) => UseInfiniteQueryResult<IListVoucherResponseAll, Error>;
}

export class VoucherService implements IVoucherService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params?: IListVoucherParams,
  ): UseQueryResult<Array<IListVoucher>, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );
    const { isVenueSpecific = false, ...restParams } = updatedParams;
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "redeemables",
          "vouchers",
          "list",
          isVenueSpecific ? "venue" : "organisation",
        ],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async () => {
        const {
          data: { results, next, count, previous },
        } = await this.api.get<AxiosResponse<IListVoucherResponse>>(
          "/promotions/vouchers/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
            params: restParams,
          },
        );

        return {
          results,
          next,
          count,
          previous,
        };
      },
    });
  }

  listAll(
    params?: IListVoucherParams,
  ): UseInfiniteQueryResult<IListVoucherResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    const { isVenueSpecific = false, ...restParams } = updatedParams;

    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "redeemables",
          "vouchers",
          "list-all",
          isVenueSpecific ? "venue" : "organisation",
        ],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IListVoucherResponse>
        >("/promotions/vouchers/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: isVenueSpecific,
          params: { ...restParams, page: pageParam, page_size: 50 },
        });

        return data;
      },
      getNextPageParam: (lastPage: IListTabResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }
}
